/**
 * HubReleaseNotesPage — RELEASE-NOTES-AUTO (#1224 / #1254).
 *
 * Review queue for agent-generated changelog drafts (published_at IS NULL,
 * generated_by='agent') — the weekly release_notes_generate task groups
 * shipped GitHub Issues into these. Approve publishes immediately (visible
 * in-app via WhatsNewModal/ChangelogPage right away); Reject keeps the row
 * (so its source issues are never re-proposed) but never surfaces it.
 *
 * Deliberately a standalone page, not a 6th /mission-control panel — see
 * docs/features/release-notes.md "Review surface" for why.
 *
 * Contract: docs/features/release-notes.md.
 */
import { useCallback, useEffect, useState } from 'react'
import { Card } from './components'
import { hubReleaseNotes, type HubPendingReleaseNote } from './api'

const GITHUB_REPO_ISSUE_BASE = 'https://github.com/meadowlarkjoe/Tenon/issues'

const CATEGORY_TONE: Record<HubPendingReleaseNote['category'], string> = {
  'New Feature': 'var(--accent-ok, #4ade80)',
  'Improvement': 'var(--accent-info, #60a5fa)',
  'Bug Fix': 'var(--accent-warn, #fbbf24)',
}

export default function HubReleaseNotesPage() {
  const [data, setData] = useState<{ items: HubPendingReleaseNote[]; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const refresh = useCallback(() => {
    hubReleaseNotes.pending()
      .then(res => { setData(res); setError(null) })
      .catch(e => setError(e.message ?? 'failed to load'))
  }, [])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [refresh])

  const act = async (id: number, action: 'publish' | 'reject') => {
    setBusyId(id)
    try {
      await (action === 'publish' ? hubReleaseNotes.publish(id) : hubReleaseNotes.reject(id))
      setData(prev => prev ? { items: prev.items.filter(n => n.id !== id), total: prev.total - 1 } : prev)
    } catch (e: any) {
      setError(e.message ?? `failed to ${action}`)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
      <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Release Notes</h1>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 640 }}>
        Weekly-generated drafts from shipped GitHub Issues. Approve makes an entry
        live immediately in the app's "What's new"; Reject dismisses it (its source
        issues won't be re-proposed).
      </p>

      <Card icon="file-text" title="Pending review" endpoint="/hub/mission/release-notes/pending">
        {error && (
          <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--accent-err, #f87171)' }}>
            {error}
          </div>
        )}
        {!data && !error && (
          <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
        )}
        {data && data.items.length === 0 && (
          <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
            Nothing pending.
          </div>
        )}
        {data && data.items.map((n, i) => (
          <div
            key={n.id}
            style={{
              padding: '12px', borderBottom: i === data.items.length - 1 ? 0 : '1px solid var(--border)',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontSize: 11, padding: '1px 6px', borderRadius: 4,
                  color: CATEGORY_TONE[n.category] ?? 'var(--text-dim)',
                  border: `1px solid ${CATEGORY_TONE[n.category] ?? 'var(--border)'}`,
                }}
              >
                {n.category}
              </span>
              {n.is_major && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Major</span>}
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{n.version}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{n.title}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{n.summary}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {n.source_issue_numbers.map(num => (
                <a
                  key={num}
                  href={`${GITHUB_REPO_ISSUE_BASE}/${num}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mono"
                  style={{ fontSize: 11, color: 'var(--text-dim)', textDecoration: 'none' }}
                >
                  #{num}
                </a>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                type="button"
                disabled={busyId === n.id}
                onClick={() => act(n.id, 'publish')}
                style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 6,
                  border: '1px solid var(--accent-ok, #4ade80)', color: 'var(--accent-ok, #4ade80)',
                  background: 'transparent', cursor: busyId === n.id ? 'default' : 'pointer',
                  opacity: busyId === n.id ? 0.5 : 1,
                }}
              >
                ✓ Approve &amp; Publish
              </button>
              <button
                type="button"
                disabled={busyId === n.id}
                onClick={() => act(n.id, 'reject')}
                style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 6,
                  border: '1px solid var(--border)', color: 'var(--text-muted)',
                  background: 'transparent', cursor: busyId === n.id ? 'default' : 'pointer',
                  opacity: busyId === n.id ? 0.5 : 1,
                }}
              >
                ✕ Reject
              </button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  )
}
